import { videoMetricRates, type VideoMetrics } from './analytics.js';
import {
  PRODUCTION_PRIMARY_VARIANT,
  PRODUCTION_VARIANT_KEYS,
  type ProductionGoal,
  type ProductionStrategy,
  type ProductionVariantKey,
} from './production.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VARYANT PERFORMANSI — açılış (hook) / fikir A/B setinin SAF çekirdeği
 * ═══════════════════════════════════════════════════════════════════════════
 * Dalga 5 (2 Eki 2026, şerit B2). Plan §4 "her şey reklam performansı için":
 * bir prodüksiyonun varyantları (`deliverablePlan.variants`, Açılış A/B/C… ya da
 * Fikir A/B…) yayına çıktıktan sonra hangisinin kazandığı BURADA karar verilir.
 *
 * TEK KOPYA (Dalga 5b Delta, 2 Eki 2026 — önceden API/worker ikiz dosyasıydı,
 * `twin-files.spec.ts` grubu silindi). İki tüketici aynı kuralla karar verir:
 *     API    `creative/production/production-performance.service.ts` (GET …/performance)
 *     worker `performance/production-variants.ts`, `production/brief/performance-context.ts`
 * Ekran "kazanan B" derken marka hafızası "kazanan C" yazmasın. Yanıt tipi
 * (`ProductionPerformanceView`) dosyanın sonunda — web paneli ve sohbet aracı
 * onu okuyor.
 *
 * Girdi: `metrics_daily` AD satırları, reklamın kreatifinin varyantına
 * (`productionRenditionVariant(Creative.rendition)`) ve platformuna bağlanmış.
 * Bağ `Ad.creativeId` — yayın yolunun kurduğu TEK köprü.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Karar kuralları (gerekçeli)
 * ───────────────────────────────────────────────────────────────────────────
 *  1. PLATFORM İÇİNDE karşılaştırılır. "Durdu" eşiği platforma göre değişiyor
 *     (Meta 3 sn, TikTok 2 sn — `VIDEO_HOOK_THRESHOLD_SEC`), kitleler ve
 *     yerleşimler de; Meta'daki A ile TikTok'taki B'yi kıyaslamak platformu
 *     ölçer, açılışı değil. En dengeli veriye sahip platform seçilir (en az
 *     gösterimli varyantın gösterimi en yüksek olan).
 *  2. EŞİK: karşılaştırılan HER varyant ≥ `VARIANT_MIN_IMPRESSIONS` gösterim.
 *     Altında oranlar gürültü — `collecting` (strategy-facts'teki 1.000 ile aynı).
 *  3. ÖLÇÜ: satış hedefinde (conversion/promo/retargeting) her varyant
 *     ≥ `VARIANT_MIN_CONVERSIONS` dönüşüm aldıysa EDB (`cpa`) — işin kendisi;
 *     yoksa video verisi varsa AÇILIŞ ORANI (`hookRate` = durdu ÷ gösterim —
 *     açılış setinin test ettiği şey tam olarak bu); o da yoksa TO (`ctr`).
 *     ROAS rapor edilir ama karar ölçüsü DEĞİL: sepet tutarı gürültüsü küçük
 *     örneklerde kazananı rastgele değiştirir.
 *  4. ANLAMLILIK: oranlarda iki oranlı z-testi (havuzlu), EDB'de Poisson oran
 *     testi (dönüşüm ÷ harcama). Kazanan için z ≥ `VARIANT_MIN_Z` (≈%95) VE
 *     göreli fark ≥ `VARIANT_MIN_LIFT`. İkisinden biri yoksa `no_clear_winner`
 *     — "%3 daha iyi" bir kazanan değil, marka hafızasına yazılmaz.
 */

export const VARIANT_MIN_IMPRESSIONS = 1_000;
export const VARIANT_MIN_CONVERSIONS = 10;
/// Kazanan ile ikinci arasında en az bu göreli fark (0,1 = %10).
export const VARIANT_MIN_LIFT = 0.1;
/// İki yönlü %95 güven.
export const VARIANT_MIN_Z = 1.96;
/// Açılış oranıyla karar için varyantın gösteriminde video verisinin en az payı.
export const VARIANT_MIN_VIDEO_COVERAGE = 0.95;

/// Satış hedefleri — EDB kararı yalnız bunlarda (yeterli dönüşüm varsa).
const SALES_GOALS: ReadonlySet<string> = new Set(['conversion', 'promo', 'retargeting']);

/// Bir reklamın bir günlük satırı — varyantına ve platformuna bağlanmış.
export interface VariantMetricRow extends Partial<VideoMetrics> {
  variant: ProductionVariantKey;
  adId: string;
  platform: string;
  /// Satırın hesabının para birimi — toplamlar yalnız tek birimde anlamlı.
  currency: string | null;
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
}

export interface VariantTotals {
  variant: ProductionVariantKey;
  adIds: string[];
  platforms: string[];
  /// Tüm satırlar aynı para birimindeyse o; karışıksa `null` (harcama/EDB de `null`).
  currency: string | null;
  spend: number | null;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number | null;
  video: VideoMetrics;
  /// Video verisi olan satırların gösterimi — açılış oranının paydası ve z-testinin denemesi.
  videoImpressions: number;
  hookRate: number | null;
  holdRate: number | null;
  completionRate: number | null;
  avgWatchMs: number | null;
  ctr: number | null;
  cpa: number | null;
  roas: number | null;
  firstDate: string | null;
  lastDate: string | null;
}

export type VariantDecisionMetric = 'hookRate' | 'ctr' | 'cpa';

export type VariantDecisionStatus =
  /// Hiçbir varyantın yayında reklamı/metriği yok.
  | 'no_data'
  /// Tek varyant ölçülüyor (tek reklam ya da diğerleri yayına çıkmadı).
  | 'single'
  /// Birden çok varyant ölçülüyor ama HİÇBİR platformda ikisi yan yana değil
  /// (A Meta'da, B TikTok'ta) — karşılaştırma platformu ölçerdi (kural 1).
  | 'not_comparable'
  /// Karşılaştırma var ama bir varyant eşiğin altında.
  | 'collecting'
  /// Eşik geçildi, fark anlamlı değil ya da küçük.
  | 'no_clear_winner'
  | 'winner';

export interface VariantDecision {
  status: VariantDecisionStatus;
  /// Kararın verildiği platform (`AdPlatform`).
  platform: string | null;
  metric: VariantDecisionMetric | null;
  winner: ProductionVariantKey | null;
  runnerUp: ProductionVariantKey | null;
  /// Kazananın ikinciye göre göreli üstünlüğü (0,25 = %25 daha iyi; EDB'de %25 daha ucuz).
  lift: number | null;
  zScore: number | null;
  /// Eşiğin altında kalan varyantlar (`collecting`).
  belowThreshold: ProductionVariantKey[];
  /// Kararın platformundaki varyant toplamları (sıralı, en iyi önce) — not/ekran metni.
  compared: VariantTotals[];
}

const VARIANT_ORDER = new Map<string, number>(
  PRODUCTION_VARIANT_KEYS.map((key, index) => [key, index]),
);

const byVariant = (a: { variant: string }, b: { variant: string }) =>
  (VARIANT_ORDER.get(a.variant) ?? 99) - (VARIANT_ORDER.get(b.variant) ?? 99);

const ratio = (a: number | null, b: number | null): number | null =>
  a !== null && b !== null && b > 0 ? a / b : null;

/** Satırlar → varyant başına toplam (varyant sırası v1…v5). */
export function aggregateVariants(rows: readonly VariantMetricRow[]): VariantTotals[] {
  const groups = new Map<ProductionVariantKey, VariantMetricRow[]>();
  for (const row of rows) {
    const list = groups.get(row.variant) ?? [];
    list.push(row);
    groups.set(row.variant, list);
  }
  return [...groups.entries()].map(([variant, list]) => totalsOf(variant, list)).sort(byVariant);
}

function totalsOf(variant: ProductionVariantKey, rows: readonly VariantMetricRow[]): VariantTotals {
  const currencies = new Set(rows.map((row) => row.currency ?? ''));
  const currency = currencies.size === 1 ? [...currencies][0] || null : null;
  const mixed = currencies.size > 1;
  let spend = 0;
  let impressions = 0;
  let clicks = 0;
  let conversions = 0;
  let revenue = 0;
  const dates = rows.map((row) => row.date).sort();
  for (const row of rows) {
    spend += row.spend;
    impressions += row.impressions;
    clicks += row.clicks;
    conversions += row.conversions;
    revenue += row.revenue;
  }
  const rates = videoMetricRates(rows);
  return {
    variant,
    adIds: [...new Set(rows.map((row) => row.adId))].sort(),
    platforms: [...new Set(rows.map((row) => row.platform))].sort(),
    currency,
    spend: mixed ? null : round(spend, 4),
    impressions,
    clicks,
    conversions: round(conversions, 4),
    revenue: mixed ? null : round(revenue, 4),
    video: rates.totals,
    videoImpressions: rates.videoImpressions,
    hookRate: rates.hookRate,
    holdRate: rates.holdRate,
    completionRate: rates.completionRate,
    avgWatchMs: rates.avgWatchMs,
    ctr: ratio(clicks, impressions),
    // Para birimi karışıksa EDB/ROAS iki birimin toplamından türerdi — anlamsız.
    cpa: mixed ? null : ratio(spend, conversions),
    roas: mixed ? null : ratio(revenue, spend),
    firstDate: dates[0] ?? null,
    lastDate: dates[dates.length - 1] ?? null,
  };
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/**
 * İki oranlı z-testi (havuzlu). `null` = hesaplanamıyor (payda 0).
 * a ve b başarı/deneme sayıları; işaret a'nın b'den iyiliği.
 */
export function twoProportionZ(
  aSuccess: number,
  aTrials: number,
  bSuccess: number,
  bTrials: number,
): number | null {
  if (aTrials <= 0 || bTrials <= 0) return null;
  const pooled = (aSuccess + bSuccess) / (aTrials + bTrials);
  const variance = pooled * (1 - pooled) * (1 / aTrials + 1 / bTrials);
  if (variance <= 0) return null;
  return (aSuccess / aTrials - bSuccess / bTrials) / Math.sqrt(variance);
}

/**
 * Poisson oran testi: dönüşüm ÷ harcama (EDB'nin tersi). a'nın oranı b'den
 * yüksekse pozitif (a DAHA UCUZ dönüşüm). `null` = hesaplanamıyor.
 */
export function poissonRateZ(
  aEvents: number,
  aExposure: number,
  bEvents: number,
  bExposure: number,
): number | null {
  if (aExposure <= 0 || bExposure <= 0) return null;
  const variance = aEvents / aExposure ** 2 + bEvents / bExposure ** 2;
  if (variance <= 0) return null;
  return (aEvents / aExposure - bEvents / bExposure) / Math.sqrt(variance);
}

/** Ölçü seçimi (dosya başı kural 3). */
export function decisionMetric(
  totals: readonly VariantTotals[],
  goal: ProductionGoal | string | null,
): VariantDecisionMetric {
  if (
    goal !== null &&
    SALES_GOALS.has(goal) &&
    totals.every(
      (item) =>
        item.conversions >= VARIANT_MIN_CONVERSIONS && item.spend !== null && item.spend > 0,
    )
  ) {
    return 'cpa';
  }
  /* Açılış oranı ancak her varyantın gösteriminin NEREDEYSE TAMAMI video
     verisi taşıyorsa (`VARIANT_MIN_VIDEO_COVERAGE`): A'nın 20 günü video'suz,
     B'ninki tam ise iki oran farklı pencerelerden gelir — o zaman TO. */
  if (
    totals.every(
      (item) =>
        item.video.video3sViews !== null &&
        item.impressions > 0 &&
        item.videoImpressions / item.impressions >= VARIANT_MIN_VIDEO_COVERAGE,
    )
  ) {
    return 'hookRate';
  }
  return 'ctr';
}

/// Ölçünün değeri — büyük daha iyi olacak biçimde (EDB'de dönüşüm/harcama).
function score(item: VariantTotals, metric: VariantDecisionMetric): number | null {
  if (metric === 'hookRate') return item.hookRate;
  if (metric === 'ctr') return item.ctr;
  return item.spend !== null && item.spend > 0 ? item.conversions / item.spend : null;
}

/**
 * Kazanan kararı (dosya başı kurallar). `variantKeys` planın varyantları —
 * planda tek varyant varsa karar `single`.
 */
export function decideVariantWinner(
  rows: readonly VariantMetricRow[],
  options: { goal: ProductionGoal | string | null; variantKeys?: readonly ProductionVariantKey[] },
): VariantDecision {
  const empty = (
    status: VariantDecisionStatus,
    extra: Partial<VariantDecision> = {},
  ): VariantDecision => ({
    status,
    platform: null,
    metric: null,
    winner: null,
    runnerUp: null,
    lift: null,
    zScore: null,
    belowThreshold: [],
    compared: [],
    ...extra,
  });

  const planned = options.variantKeys ?? [];
  const relevant = rows.filter((row) => planned.length === 0 || planned.includes(row.variant));
  if (relevant.length === 0) return empty('no_data');

  const byPlatform = new Map<string, VariantMetricRow[]>();
  for (const row of relevant) {
    const list = byPlatform.get(row.platform) ?? [];
    list.push(row);
    byPlatform.set(row.platform, list);
  }

  // Kural 1 — iki ya da daha çok varyantın ölçüldüğü platformlardan en dengelisi.
  let best: { platform: string; totals: VariantTotals[]; floor: number; total: number } | null =
    null;
  for (const [platform, list] of byPlatform) {
    const totals = aggregateVariants(list);
    if (totals.length < 2) continue;
    const floor = Math.min(...totals.map((item) => item.impressions));
    const total = totals.reduce((sum, item) => sum + item.impressions, 0);
    if (
      !best ||
      floor > best.floor ||
      (floor === best.floor &&
        (total > best.total || (total === best.total && platform < best.platform)))
    ) {
      best = { platform, totals, floor, total };
    }
  }

  if (!best) {
    const overall = aggregateVariants(relevant);
    return empty(overall.length > 1 ? 'not_comparable' : 'single', {
      platform: overall.length === 1 ? (overall[0]?.platforms[0] ?? null) : null,
      compared: overall,
    });
  }

  const { platform, totals } = best;
  const below = totals
    .filter((item) => item.impressions < VARIANT_MIN_IMPRESSIONS)
    .map((item) => item.variant);
  if (below.length > 0) {
    return empty('collecting', { platform, belowThreshold: below, compared: totals });
  }

  const metric = decisionMetric(totals, options.goal);
  const ranked = [...totals].sort(
    (a, b) => (score(b, metric) ?? -1) - (score(a, metric) ?? -1) || byVariant(a, b),
  );
  const [first, second] = ranked;
  if (!first || !second) return empty('single', { platform, compared: ranked });

  const firstScore = score(first, metric);
  const secondScore = score(second, metric);
  const lift =
    firstScore !== null && secondScore !== null && secondScore > 0
      ? firstScore / secondScore - 1
      : null;
  const z =
    metric === 'cpa'
      ? poissonRateZ(first.conversions, first.spend ?? 0, second.conversions, second.spend ?? 0)
      : metric === 'hookRate'
        ? twoProportionZ(
            first.video.video3sViews ?? 0,
            first.videoImpressions,
            second.video.video3sViews ?? 0,
            second.videoImpressions,
          )
        : twoProportionZ(first.clicks, first.impressions, second.clicks, second.impressions);

  const decided = lift !== null && lift >= VARIANT_MIN_LIFT && z !== null && z >= VARIANT_MIN_Z;
  return {
    status: decided ? 'winner' : 'no_clear_winner',
    platform,
    metric,
    winner: decided ? first.variant : null,
    runnerUp: decided ? second.variant : null,
    lift: lift === null ? null : round(lift, 4),
    zScore: z === null ? null : round(z, 3),
    belowThreshold: [],
    compared: ranked,
  };
}

/** Planın varyant anahtarları — plan yoksa yalnız birincil. */
export function plannedVariantKeys(
  plan: { variants?: readonly { key: string }[] } | null | undefined,
): ProductionVariantKey[] {
  const keys = (plan?.variants ?? [])
    .map((variant) => variant.key)
    .filter((key): key is ProductionVariantKey =>
      (PRODUCTION_VARIANT_KEYS as readonly string[]).includes(key),
    );
  return keys.length > 0 ? keys : [PRODUCTION_PRIMARY_VARIANT];
}

// ═══════════════════════════════════════════════════════════════════════════
// `metrics_daily` satırı → varyant satırı (iki tüketicinin ortak girişi)
// ═══════════════════════════════════════════════════════════════════════════

type Numeric = { toNumber(): number } | number | bigint | null;

/// Prisma Decimal / BigInt / null → sayı (`null` korunur).
export function numOrNull(value: Numeric | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  return value.toNumber();
}

export interface PerformanceMetricRow {
  entityId: string;
  date: Date;
  spend: Numeric;
  impressions: Numeric;
  clicks: Numeric;
  conversions: Numeric;
  revenue: Numeric;
  videoPlays: Numeric;
  video3sViews: Numeric;
  videoThruplays: Numeric;
  videoP25: Numeric;
  videoP50: Numeric;
  videoP75: Numeric;
  videoP100: Numeric;
  videoAvgWatchMs: Numeric;
}

/**
 * `metrics_daily` AD satırları → varyant satırları. Varyantı bilinmeyen
 * (kreatifi prodüksiyon dışı ya da silinmiş) reklamın satırı DÜŞER — başka bir
 * varyantın toplamına yazılmaz.
 */
export function toVariantRows(
  metrics: readonly PerformanceMetricRow[],
  ads: ReadonlyMap<
    string,
    { variant: ProductionVariantKey | null; platform: string; currency: string | null }
  >,
): VariantMetricRow[] {
  const out: VariantMetricRow[] = [];
  for (const row of metrics) {
    const ad = ads.get(row.entityId);
    if (!ad || !ad.variant) continue;
    const video: VideoMetrics = {
      videoPlays: numOrNull(row.videoPlays),
      video3sViews: numOrNull(row.video3sViews),
      videoThruplays: numOrNull(row.videoThruplays),
      videoP25: numOrNull(row.videoP25),
      videoP50: numOrNull(row.videoP50),
      videoP75: numOrNull(row.videoP75),
      videoP100: numOrNull(row.videoP100),
      videoAvgWatchMs: numOrNull(row.videoAvgWatchMs),
    };
    out.push({
      variant: ad.variant,
      adId: row.entityId,
      platform: ad.platform,
      currency: ad.currency,
      date: row.date.toISOString().slice(0, 10),
      spend: numOrNull(row.spend) ?? 0,
      impressions: numOrNull(row.impressions) ?? 0,
      clicks: numOrNull(row.clicks) ?? 0,
      conversions: numOrNull(row.conversions) ?? 0,
      revenue: numOrNull(row.revenue) ?? 0,
      ...video,
    });
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt — `GET /creatives/productions/:id/performance` (Dalga 5b'de API'den taşındı)
// ═══════════════════════════════════════════════════════════════════════════

export interface ProductionPerformanceVariantView {
  key: ProductionVariantKey;
  /// Planın kullanıcı dilindeki adı ("Soru: Ateşin var mı?"); planda yoksa `null`.
  label: string | null;
  rationale: string | null;
  kind: 'single' | 'hook' | 'concept' | null;
  /// Bu varyantın teslimleri (her boyut/kesim ayrı kreatif).
  creativeIds: string[];
  /// Bu varyantın kreatiflerine bağlı reklam sayısı (metriği olmasa da).
  adCount: number;
  /// Tüm platformların toplamı — para birimi karışıksa harcama/EDB/ROAS `null`.
  total: VariantTotals | null;
  /// Platform başına toplam (kararın tabanı; para birimi her birinde tek).
  byPlatform: VariantTotals[];
}

export interface ProductionPerformanceView {
  productionId: string;
  strategy: ProductionStrategy | null;
  goal: ProductionGoal | null;
  variants: ProductionPerformanceVariantView[];
  decision: VariantDecision;
  /// Kararın eşikleri — ekran "1.000 gösterim bekleniyor" diyebilsin.
  thresholds: { minImpressionsPerVariant: number; minConversions: number; minLift: number };
  /// "Durdu" eşiği (sn) — yalnız verisi olan platformlar (Meta 3, TikTok 2).
  hookThresholdSec: Record<string, number>;
  /// En son senkron edilen metrik satırı — "son güncelleme".
  syncedAt: string | null;
}
